// =========================  DbConnectionsView.jsx  =========================

import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  Save,
  Check,
  Trash2,
  Edit3,
  Database,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import "./DbConnectionsView.css";

const DB_ENGINES = {
  postgres: { label: "PostgreSQL", defaults: { port: 5432 }, fields: ["host", "port", "database", "user", "password"] },
  mysql:    { label: "MySQL / MariaDB", defaults: { port: 3306 }, fields: ["host", "port", "database", "user", "password"] },
  sqlserver:{ label: "SQL Server", defaults: { port: 1433 }, fields: ["host", "port", "database", "user", "password"] },
  
  mongodb:  { label: "MongoDB", defaults: { port: 27017 }, fields: ["host", "port", "database", "user", "password"] },
  redis:    { label: "Redis", defaults: { port: 6379 }, fields: ["host", "port", "password"] },

};

export default function DbConnectionsView() {
  const { t } = useTranslation();

  const [engine, setEngine] = useState("postgres");
  const [showForm, setShowForm] = useState(true);
  const [form, setForm] = useState({ name: "", host: "", port: DB_ENGINES.postgres.defaults.port, database: "", serviceName: "", filePath: "", user: "", password: "" });
  const [tested, setTested] = useState(false);
  const [testing, setTesting] = useState(false);

  const [connections, setConnections] = useState([]);

  const [toast, setToast] = useState({ message: "", visible: false });
  useEffect(() => {
    if (!toast.visible) return;
    const id = setTimeout(() => setToast(v => ({ ...v, visible: false })), 3000);
    return () => clearTimeout(id);
  }, [toast.visible]);

  const [confirm, setConfirm] = useState({ open: false, id: null });

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const switchEngine = e => {
    const eng = e.target.value;
    setEngine(eng);
    setForm(f => ({ ...f, port: DB_ENGINES[eng].defaults.port || "" }));
  };

  const testConnection = async () => {
    setTesting(true);
    await new Promise(r => setTimeout(r, 800));
    setTested(true);
    setTesting(false);
  };

  const saveConnection = () => {
    if (!tested) return;
    const newConn = { id: Date.now(), engine, ...form };
    setConnections(prev => [...prev, newConn]);
    setToast({ message: t("dbConnections.toastSaved"), visible: true });
    setTested(false);
    setForm({ name: "", host: "", port: DB_ENGINES[engine].defaults.port, database: "", serviceName: "", filePath: "", user: "", password: "" });
    setShowForm(false);
  };

  const requestDelete = id => setConfirm({ open: true, id });
  const cancelDelete = () => setConfirm({ open: false, id: null });
  const confirmDelete = () => {
    setConnections(prev => prev.filter(c => c.id !== confirm.id));
    setToast({ message: t("dbConnections.toastDeleted"), visible: true });
    setConfirm({ open: false, id: null });
  };

  const FIELDS = DB_ENGINES[engine].fields;

  return (
    <div className="db-connections-view">
      <h1 className="page-title">
        <Database size={24} style={{ marginRight: 8, verticalAlign: "middle" }} />
        {t("dbConnections.title")}
      </h1>
      <p className="page-desc">{t("dbConnections.pageDesc")}</p>

      {toast.visible && <div className="toast">{toast.message}</div>}

      {connections.length > 0 && (
        <div className="connections-list">
          {connections.map(c => (
            <div key={c.id} className="card connection-card">
              <div className="card-header">
                <strong>{c.name || t("dbConnections.noName")}</strong>
                <em>({DB_ENGINES[c.engine].label})</em>
                <div className="actions">
                  <button className="icon-btn"><Edit3 size={16} /></button>
                  <button className="icon-btn" onClick={() => requestDelete(c.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card create-connection-card">
        <p className="card-desc">{t("dbConnections.cardDesc")}</p>
        <button className="collapse-toggle" onClick={() => setShowForm(f => !f)}>
          <span>{t("dbConnections.newConnection")}</span>
          {showForm ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {showForm && (
          <div className="form-wrapper">
            <div className="fields-row">
              <div className="field">
                <label>{t("dbConnections.displayName")}</label>
                <input name="name" placeholder={t("dbConnections.displayName")} value={form.name} onChange={handleChange} />
              </div>
              <div className="field">
                <label>{t("dbConnections.engine")}</label>
                <select value={engine} onChange={switchEngine}>
                  {Object.entries(DB_ENGINES).map(([key, meta]) => (
                    <option key={key} value={key}>{meta.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {FIELDS.includes("filePath") ? (
              <div className="field full">
                <label>{t("dbConnections.sqliteFile")}</label>
                <input name="filePath" placeholder="/path/to/my.db" value={form.filePath} onChange={handleChange} />
              </div>
            ) : (
              <>
                <div className="fields-row">
                  <div className="field">
                    <label>{t("dbConnections.host")}</label>
                    <input name="host" placeholder="db.example.com" value={form.host} onChange={handleChange} />
                  </div>
                  <div className="field">
                    <label>{t("dbConnections.port")}</label>
                    <input name="port" type="number" value={form.port} onChange={handleChange} />
                  </div>
                </div>

                {FIELDS.includes("database") && (
                  <div className="field full">
                    <label>{t("dbConnections.database")}</label>
                    <input name="database" placeholder="main_db" value={form.database} onChange={handleChange} />
                  </div>
                )}
                {FIELDS.includes("serviceName") && (
                  <div className="field full">
                    <label>{t("dbConnections.serviceName")}</label>
                    <input name="serviceName" value={form.serviceName} onChange={handleChange} />
                  </div>
                )}
                {FIELDS.includes("user") && (
                  <div className="fields-row">
                    <div className="field">
                      <label>{t("dbConnections.user")}</label>
                      <input name="user" placeholder="db_user" value={form.user} onChange={handleChange} />
                    </div>
                    <div className="field">
                      <label>{t("dbConnections.password")}</label>
                      <input type="password" name="password" value={form.password} onChange={handleChange} />
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="actions">
              <button className="btn primary" onClick={testConnection} disabled={testing}>
                {testing ? t("dbConnections.testConnection") + "..." : t("dbConnections.testConnection")}  
              </button>
              <button className="btn secondary" onClick={saveConnection} disabled={!tested}>
                <Save size={16} /> {t("dbConnections.save")}
              </button>
              {tested && (
                <span className="test-ok">
                  <Check size={16} /> {t("dbConnections.ok")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {confirm.open && (
        <div className="modal-backdrop" onClick={cancelDelete}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>{t("dbConnections.confirmTitle")}</h3>
            <p>{t("dbConnections.confirmText")}</p>
            <div className="modal-actions">
              <button className="btn secondary" onClick={cancelDelete}>{t("dbConnections.cancel")}</button>
              <button className="btn primary" onClick={confirmDelete}>{t("dbConnections.delete")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
